import React, { useEffect, useMemo, useState } from 'react';

const loadingHero = '/assets/RoddoPrivacyMistScene.png';

type LoadingScreenMode = 'fullscreen' | 'overlay';

interface LoadingScreenProps {
  label?: string;
  detail?: string;
  mode?: LoadingScreenMode;
  visible?: boolean;
  className?: string;
  respectBottomNav?: boolean;
  progress?: number;
  segmented?: boolean;
}

const FADE_MS = 220;

const LoadingScreen: React.FC<LoadingScreenProps> = ({
  label = 'Opening Roddo…',
  detail,
  mode = 'fullscreen',
  visible = true,
  className = '',
  respectBottomNav = false,
  progress = 8,
  segmented = false,
}) => {
  const safeProgress = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)));
  const [isMounted, setIsMounted] = useState(visible);
  const [isActive, setIsActive] = useState(visible);
  const segmentCount = 6;
  const filledSegments = Math.max(
    0,
    Math.min(segmentCount, Math.ceil((safeProgress / 100) * segmentCount)),
  );

  useEffect(() => {
    if (visible) {
      setIsMounted(true);
      const raf = window.requestAnimationFrame(() => setIsActive(true));
      return () => window.cancelAnimationFrame(raf);
    }

    setIsActive(false);
    const timer = window.setTimeout(() => setIsMounted(false), FADE_MS);
    return () => window.clearTimeout(timer);
  }, [visible]);

  const shellClass = useMemo(
    () =>
      mode === 'overlay'
        ? 'absolute inset-0 z-[1250] pointer-events-none'
        : respectBottomNav
          ? 'fixed left-0 right-0 top-0 z-[4000] flex items-start justify-center pointer-events-auto'
          : 'fixed inset-0 z-[4000] flex items-start justify-center pointer-events-auto',
    [mode, respectBottomNav],
  );

  if (!isMounted) return null;

  if (mode === 'overlay') {
    return (
      <div
        aria-live="polite"
        aria-busy={visible}
        className={`${shellClass} ${className}`.trim()}
        style={{
          opacity: isActive ? 1 : 0,
          transition: `opacity ${FADE_MS}ms ease`,
        }}
      >
        <div className="absolute inset-0 overflow-hidden bg-[#dfeff6] dark:bg-[#070B15]">
          <img
            src={loadingHero}
            alt=""
            className="h-full w-full object-cover object-center opacity-95 dark:opacity-75"
            draggable={false}
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,.04)_0%,rgba(255,255,255,.01)_55%,rgba(10,17,32,.28)_100%)] dark:bg-[linear-gradient(180deg,rgba(7,11,21,.12)_0%,rgba(7,11,21,.24)_58%,rgba(7,11,21,.58)_100%)]" />
        </div>

        <div className="absolute inset-x-0 bottom-[30px] px-6 text-center">
          <div className="mx-auto w-full max-w-[310px]">
            {segmented ? (
              <div className="grid grid-cols-6 gap-1.5">
                {Array.from({ length: segmentCount }).map((_, index) => (
                  <div
                    key={index}
                    className="h-1.5 overflow-hidden rounded-full bg-white/42 ring-1 ring-[#173A57]/10 dark:bg-white/10 dark:ring-white/10"
                  >
                    <div
                      className="h-full rounded-full bg-[#2F80ED] transition-opacity duration-200 dark:bg-[#67D8E8]"
                      style={{ opacity: index < filledSegments ? 1 : 0 }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/48 ring-1 ring-[#173A57]/10 dark:bg-white/10 dark:ring-white/10">
                <div
                  className="h-full rounded-full bg-[#2F80ED] shadow-[0_0_10px_rgba(47,128,237,.20)] transition-[width] duration-300 ease-out dark:bg-[#67D8E8] dark:shadow-none"
                  style={{ width: `${safeProgress}%` }}
                />
              </div>
            )}
            <div className="mt-3 text-[11px] font-black tracking-[0.11em] text-[#173A57]/72 dark:text-[#E8EEF7]/78">
              {label}
            </div>
            {detail ? (
              <div className="mt-1.5 text-[10px] font-extrabold tracking-[0.05em] text-[#173A57]/56 dark:text-[#A8B4C7]/78">
                {detail}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      aria-live="polite"
      aria-busy={visible}
      className={`${shellClass} ${className}`.trim()}
      style={{
        opacity: isActive ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease`,
        background: '#F7F2E8',
        ...(respectBottomNav
          ? { bottom: 'var(--roddo-nav-h, 76px)' }
          : null),
      }}
    >
      <div
        className="relative h-full w-full overflow-hidden bg-[#dfeff6] md:max-w-[760px] md:rounded-[28px] lg:max-w-none lg:rounded-none"
        style={{
          height: respectBottomNav
            ? 'calc(var(--roddo-app-vh, var(--roddo-vh, 100dvh)) - var(--roddo-nav-h, 76px))'
            : 'var(--roddo-app-vh, var(--roddo-vh, 100dvh))',
          minHeight: respectBottomNav
            ? 'calc(var(--roddo-app-vh, var(--roddo-vh, 100dvh)) - var(--roddo-nav-h, 76px))'
            : 'var(--roddo-app-vh, var(--roddo-vh, 100dvh))',
          maxHeight: respectBottomNav
            ? 'calc(var(--roddo-app-vh, var(--roddo-vh, 100dvh)) - var(--roddo-nav-h, 76px))'
            : 'var(--roddo-app-vh, var(--roddo-vh, 100dvh))',
          boxShadow: '0 18px 48px rgba(48,39,30,.14)',
        }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={loadingHero}
            alt=""
            className="h-full w-full object-cover object-center"
            draggable={false}
          />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,.08)_0%,rgba(255,255,255,.02)_56%,rgba(18,35,47,.20)_100%)]" />
          <div className="absolute inset-x-0 bottom-0 h-[34%] bg-gradient-to-t from-[#143047]/32 via-[#143047]/10 to-transparent" />
        </div>

        <div className="absolute inset-0 px-6 text-center">
          <div className="absolute inset-x-0 top-[39%] -translate-y-1/2 px-6 sm:top-[40%] lg:top-[38%]">
            <div className="mx-auto flex max-w-[360px] flex-col items-center">
              <img
                src="/assets/woodensign.png"
                alt="Roddo — Your Pocket Fishing Buddy"
                className="h-auto w-[235px] drop-shadow-[0_9px_22px_rgba(31,65,86,.20)] sm:w-[255px] lg:w-[300px] xl:w-[325px]"
                draggable={false}
              />
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+30px)] mx-auto w-[calc(100%-48px)] max-w-[310px] lg:bottom-[42px] lg:max-w-[390px] xl:max-w-[430px]">
            <div className="mb-4 text-[12px] font-black uppercase tracking-[0.28em] text-[#173A57]/78 drop-shadow-[0_1px_8px_rgba(255,255,255,.78)] sm:text-[13px] lg:mb-5 lg:text-[14px] lg:tracking-[0.32em]">
              Explore. Log. Connect.
            </div>
            {segmented ? (
              <div className="grid grid-cols-6 gap-1.5">
                {Array.from({ length: segmentCount }).map((_, index) => (
                  <div
                    key={index}
                    className="h-1.5 overflow-hidden rounded-full bg-white/42 shadow-[0_1px_0_rgba(255,255,255,.40)] ring-1 ring-[#173A57]/10 backdrop-blur-sm"
                  >
                    <div
                      className="h-full rounded-full bg-[#2F80ED]/88 transition-opacity duration-200"
                      style={{ opacity: index < filledSegments ? 1 : 0 }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="mx-auto h-1.5 w-full overflow-hidden rounded-full bg-white/48 shadow-[0_1px_0_rgba(255,255,255,.55)] ring-1 ring-[#173A57]/10 backdrop-blur-sm">
                <div className="h-full rounded-full bg-[#2F80ED]/88 shadow-[0_0_10px_rgba(47,128,237,.20)] transition-[width] duration-300 ease-out" style={{ width: `${safeProgress}%` }} />
              </div>
            )}
            <div className="mt-3 text-[11px] font-black tracking-[0.11em] text-[#173A57]/62 drop-shadow-[0_1px_7px_rgba(255,255,255,.8)] lg:mt-4 lg:text-[12px] lg:tracking-[0.14em]">
              {label}
            </div>
            {detail ? (
              <div className="mt-1.5 text-[10px] font-extrabold tracking-[0.06em] text-[#173A57]/52 drop-shadow-[0_1px_7px_rgba(255,255,255,.82)] lg:text-[11px]">
                {detail}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes roddo-loader-line {
          0% { transform: translateX(-125%); }
          52% { transform: translateX(105%); }
          100% { transform: translateX(235%); }
        }
        .roddo-loader-line { animation: roddo-loader-line 1.7s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .roddo-loader-line { animation-duration: 3.4s; }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
